var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { createUseStyles } from 'react-jss';
import { useTheme } from '../../theme';
import { useMemoClasses } from '../../utils/hooks';
var useStyles = createUseStyles({
    base: {
        flexShink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
        margin: 'var(--RUI-margin)',
        cursor: 'pointer',
        font: 'var(--RUI-font-input)',
        userSelect: 'none',
        '&:hover $checkmark': {
            backgroundColor: 'var(--RUI-checkbox-light)',
        },
    },
    baseOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Checkbox.base));
    },
    input: {
        position: 'absolute',
        left: '-10000px',
        top: 'auto',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
        '&:checked ~ $checkmark': {
            backgroundColor: 'var(--RUI-checkbox-main)',
        },
        '&:checked ~ $checkmark::after': {
            transform: 'scale(1) rotate(45deg)',
        },
    },
    checkmark: {
        display: 'block',
        position: 'relative',
        height: '15px',
        width: '15px',
        borderRadius: 'var(--RUI-border-radius)',
        backgroundColor: 'var(--RUI-color-neutral-lighter)',
        '&:not(:last-child)': {
            marginRight: 'var(--RUI-margin)',
        },
        '&::after': {
            position: 'absolute',
            display: 'block',
            content: '""',
            left: '36%',
            top: '20%',
            width: '20%',
            height: '40%',
            border: '3px solid var(--RUI-checkbox-contrast)',
            borderTop: 0,
            borderLeft: 0,
            transform: 'scale(0) rotate(45deg)',
            transition: 'transform 0.2s',
        },
    },
    checkmarkOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Checkbox.checkmark));
    },
    indeterminate: {
        '&::after': {
            borderBottom: 0,
            transform: 'scale(0) rotate(90deg)',
        },
    },
    indeterminateInput: {
        '&:checked ~ $checkmark::after': {
            transform: 'scale(1) rotate(90deg)',
        },
    },
    small: {
        height: '10px',
        width: '10px',
    },
    smallOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Radio.small));
    },
    big: {
        height: '25px',
        width: '25px',
    },
    bigOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Radio.big));
    },
    label: {},
    labelOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Checkbox.label));
    },
    primary: {
        '--RUI-checkbox-main': 'var(--RUI-color-primary)',
        '--RUI-checkbox-light': 'var(--RUI-color-primary-light)',
        '--RUI-checkbox-contrast': 'var(--RUI-color-primary-contrast)',
    },
    secondary: {
        '--RUI-checkbox-main': 'var(--RUI-color-secondary)',
        '--RUI-checkbox-light': 'var(--RUI-color-secondary-light)',
        '--RUI-checkbox-contrast': 'var(--RUI-color-secondary-contrast)',
    },
    neutral: {
        '--RUI-checkbox-main': 'var(--RUI-color-neutral)',
        '--RUI-checkbox-light': 'var(--RUI-color-neutral-light)',
        '--RUI-checkbox-contrast': 'var(--RUI-color-neutral-contrast)',
    },
    error: {
        '--RUI-checkbox-main': 'var(--RUI-color-error)',
        '--RUI-checkbox-light': 'var(--RUI-color-error-light)',
        '--RUI-checkbox-contrast': 'var(--RUI-color-error-contrast)',
    },
    warning: {
        '--RUI-checkbox-main': 'var(--RUI-color-warning)',
        '--RUI-checkbox-light': 'var(--RUI-color-warning-light)',
        '--RUI-checkbox-contrast': 'var(--RUI-color-warning-contrast)',
    },
    success: {
        '--RUI-checkbox-main': 'var(--RUI-color-success)',
        '--RUI-checkbox-light': 'var(--RUI-color-success-light)',
        '--RUI-checkbox-contrast': 'var(--RUI-color-success-contrast)',
    },
    disabled: {
        cursor: 'not-allowed',
        color: 'var(--RUI-color-neutral-lighter)',
        '&:hover $input + $checkmark': {
            backgroundColor: 'var(--RUI-color-neutral-lighter)',
        },
    },
}, { name: 'RUICheckbox' });
var Checkbox = function (_a) {
    var color = _a.color, label = _a.label, size = _a.size, indeterminate = _a.indeterminate, disabled = _a.disabled, className = _a.className, id = _a.id, style = _a.style, props = __rest(_a, ["color", "label", "size", "indeterminate", "disabled", "className", "id", "style"]);
    var theme = useTheme();
    var styles = useStyles({ theme: theme });
    var baseClasses = useMemoClasses([
        styles.base,
        styles[color],
        disabled && styles.disabled,
        theme.overrides.Checkbox.base && styles.baseOverrides,
        'RUICheckbox-base',
        className,
    ], [theme, color, disabled, className]);
    var checkmarkClasses = useMemoClasses([
        styles.checkmark,
        size === 'small' && styles.small,
        size === 'small' && theme.overrides.Checkbox.small && styles.smallOverrides,
        size === 'big' && styles.big,
        size === 'big' && theme.overrides.Checkbox.big && styles.bigOverrides,
        disabled && styles.disabled,
        indeterminate && styles.indeterminate,
        theme.overrides.Checkbox.checkmark && styles.checkmarkOverrides,
        'RUICheckbox-checkmark',
    ], [theme, indeterminate, disabled, size]);
    var inputClasses = useMemoClasses([styles.input, disabled && styles.disabled, indeterminate && styles.indeterminateInput, 'RUICheckbox-input'], [theme, indeterminate, disabled]);
    var labelClasses = useMemoClasses([
        styles.label,
        disabled && styles.disabled,
        theme.overrides.Checkbox.label && styles.labelOverrides,
        'RUICheckbox-label',
    ], [theme, disabled]);
    return (React.createElement("label", { style: style, className: baseClasses, htmlFor: id },
        React.createElement("input", __assign({ id: id, disabled: disabled, className: inputClasses, type: "checkbox" }, props)),
        React.createElement("span", { className: checkmarkClasses }),
        typeof label === 'string' ? React.createElement("span", { className: labelClasses }, label) : label));
};
Checkbox.defaultProps = {
    color: 'primary',
    label: null,
    size: 'normal',
    indeterminate: false,
};
export { Checkbox as default, Checkbox };
